import { publishReadinessItems } from "./listing-readiness-items";

export type ListingReadinessTier = "required" | "great" | "excellent";

export type ListingPublishLevel = "not_ready" | "ready" | "great" | "excellent";

export type ListingReadinessTab = "details" | "financials" | "photos";

export type ListingPublishReadinessItem = {
  id: string;
  label: string;
  todo: string;
  tier: ListingReadinessTier;
  done: boolean;
  tab: ListingReadinessTab;
  /** Element id to scroll to on the editor tab. */
  anchor: string;
};

export type ListingPublishReadinessInput = {
  title: string | null;
  businessTypeId: number | null;
  saleType: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  establishedAt: Date | string | null;
  pitch: string | null;
  description: string | null;
  askingPrice: bigint | number | string | null;
  revenue: bigint | number | string | null;
  sde: bigint | number | string | null;
  ebitda: bigint | number | string | null;
  profit: bigint | number | string | null;
  photoCount: number;
  propertyKind: "OWNED" | "RENTED" | "NONE" | null;
  rentAmount: bigint | number | string | null;
  leaseExpiration: Date | string | null;
  employeeCount: number | string | null;
  ownerWeeklyHours: number | string | null;
  financingAvailable: boolean | null;
  sbaPrequalified: boolean | null;
  minimumDownPayment: bigint | number | string | null;
  buyerCanAssumeLoan: boolean | null;
  trainingAvailable: boolean | null;
  trainingDuration: string | null;
  websiteUrl: string | null;
};

export type ListingPublishReadiness = {
  items: ListingPublishReadinessItem[];
  level: ListingPublishLevel;
  requiredDone: number;
  requiredTotal: number;
  greatRemaining: number;
  excellentRemaining: number;
  readyToPublish: boolean;
  /** First unmet item, required before quality. */
  next: ListingPublishReadinessItem | null;
};

/** What a listing needs before publish, then what makes it great or excellent. */
export function listingPublishReadiness(
  input: ListingPublishReadinessInput,
): ListingPublishReadiness {
  const items = publishReadinessItems(input);

  const required = items.filter((entry) => entry.tier === "required");
  const great = items.filter((entry) => entry.tier === "great");
  const excellent = items.filter((entry) => entry.tier === "excellent");
  const requiredDone = required.filter((entry) => entry.done).length;
  const greatRemaining = great.filter((entry) => !entry.done).length;
  const excellentRemaining = excellent.filter((entry) => !entry.done).length;
  const requiredComplete = requiredDone === required.length;

  let level: ListingPublishLevel = "not_ready";
  if (requiredComplete && greatRemaining === 0 && excellentRemaining === 0) {
    level = "excellent";
  } else if (requiredComplete && greatRemaining === 0) {
    level = "great";
  } else if (requiredComplete) {
    level = "ready";
  }

  return {
    items,
    level,
    requiredDone,
    requiredTotal: required.length,
    greatRemaining,
    excellentRemaining,
    readyToPublish: requiredComplete,
    next: items.find((entry) => !entry.done) ?? null,
  };
}
